import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert/Alert';
import { Building2, Users, Star, Sparkles, MapPin, FileText, X, Repeat, Globe, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { VenueResearch } from '@/services/VenueResearchService';

/**
 * Validation schema for research form
 */
const researchSchema = z.object({
  search_cities: z.array(
    z.object({
      city: z.string().min(3),
      place_id: z.string().optional(),
    })
  ).min(1, 'At least one city is required').max(10, 'Maximum 10 cities allowed'),
  search_sequences: z.number().int().min(1).max(5),
  min_rooms: z.number().int().positive().optional(),
  min_conference_capacity: z.number().int().positive().optional(),
  min_stars: z.number().int().min(1).max(5).optional(),
  additional_instructions: z.string().max(500, 'Instructions must be 500 characters or less').optional(),
});

export interface ResearchFormData {
  search_cities: Array<{
    city: string;
    place_id?: string;
    city_only?: string;
    location_type?: 'city' | 'country' | 'region';
  }>;
  search_sequences: number;
  min_rooms?: number;
  min_conference_capacity?: number;
  min_stars?: number;
  additional_instructions?: string;
}

export interface ResearchFormProps {
  onSubmit: (data: ResearchFormData) => Promise<void>;
  isSubmitting?: boolean;
  initialValues?: Partial<ResearchFormData>;
  hideHeader?: boolean; // Hide Card header when used in modal
  activeResearchJobs?: VenueResearch[]; // Active research jobs for selected cities
}

export function ResearchForm({ onSubmit, isSubmitting = false, initialValues, hideHeader = false, activeResearchJobs = [] }: ResearchFormProps) {
  const [cities, setCities] = useState<Array<{ city: string; place_id?: string; city_only?: string; location_type?: 'city' | 'country' | 'region' }>>(
    initialValues?.search_cities || []
  );
  const [cityInput, setCityInput] = useState(''); // Current input value
  const [searchSequences, setSearchSequences] = useState<string>(
    initialValues?.search_sequences?.toString() || '1'
  );
  const [minRooms, setMinRooms] = useState<string>(
    initialValues?.min_rooms?.toString() || ''
  );
  const [minCapacity, setMinCapacity] = useState<string>(
    initialValues?.min_conference_capacity?.toString() || ''
  );
  const [minStars, setMinStars] = useState<string>(
    initialValues?.min_stars?.toString() || ''
  );
  const [additionalInstructions, setAdditionalInstructions] = useState<string>(
    initialValues?.additional_instructions || ''
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when initialValues change (e.g., when modal opens with new pre-filled data)
  useEffect(() => {
    if (initialValues) {
      setCities(initialValues.search_cities || []);
      setSearchSequences(initialValues.search_sequences?.toString() || '1');
      setMinRooms(initialValues.min_rooms?.toString() || '');
      setMinCapacity(initialValues.min_conference_capacity?.toString() || '');
      setMinStars(initialValues.min_stars?.toString() || '');
      setAdditionalInstructions(initialValues.additional_instructions || '');
    }
  }, [initialValues]);

  // Filter active research jobs by current cities in the form (updates as user adds/removes cities)
  const matchingActiveJobs = React.useMemo(() => {
    if (!activeResearchJobs.length || cities.length === 0) return [];
    
    return activeResearchJobs.filter((job: VenueResearch) => {
      const jobCity = job.search_city?.toLowerCase().trim() || '';
      if (!jobCity) return false;
      
      const jobCityName = jobCity.split(',')[0].trim();
      const jobPlaceId = job.search_city_place_id;
      
      return cities.some(cityData => {
        const formCity = cityData.city?.toLowerCase().trim() || '';
        const cityOnly = cityData.city_only?.toLowerCase().trim() || '';
        const formPlaceId = cityData.place_id;
        
        // Priority 1: Match by place_id if both have it (most precise)
        if (jobPlaceId && formPlaceId && jobPlaceId === formPlaceId) {
          return true;
        }
        
        // Priority 2: Exact string matches
        // Exact match: full city string
        if (jobCity === formCity) return true;
        
        // Exact match: city only (if provided)
        if (cityOnly && jobCityName === cityOnly) return true;
        
        // Format match: job has format "city, country" and city name matches
        if (cityOnly && jobCityName === cityOnly && jobCity.includes(formCity.split(',')[1]?.trim() || '')) {
          return true;
        }
        
        return false;
      });
    });
  }, [activeResearchJobs, cities]);

  const handleLocationSelect = (location: LocationData) => {
    // Add location (city or country) if not already added
    const locationExists = cities.some(
      (c) => c.city.toLowerCase() === location.formatted_address.toLowerCase()
    );
    
    if (!locationExists) {
      setCities([
        ...cities,
        {
          city: location.formatted_address, // Full formatted address (e.g., "Toronto, ON, Canada" or "Spain")
          place_id: location.place_id,
          city_only: location.city || location.formatted_address.split(',')[0].trim(), // Locality from Google Places
          location_type: location.location_type || 'city', // Track if it's a city or country
        },
      ]);
    }
    
    setCityInput(''); // Clear the input after selection
    setErrors((prev) => ({ ...prev, search_cities: '' }));
  };
  
  const removeCity = (index: number) => {
    setCities(cities.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Build form data
    const formData: ResearchFormData = {
      search_cities: cities,
      search_sequences: parseInt(searchSequences, 10) || 1,
    };

    if (minRooms) {
      const rooms = parseInt(minRooms, 10);
      if (!isNaN(rooms) && rooms > 0) {
        formData.min_rooms = rooms;
      }
    }

    if (minCapacity) {
      const capacity = parseInt(minCapacity, 10);
      if (!isNaN(capacity) && capacity > 0) {
        formData.min_conference_capacity = capacity;
      }
    }

    if (minStars) {
      const stars = parseInt(minStars, 10);
      if (!isNaN(stars) && stars >= 1 && stars <= 5) {
        formData.min_stars = stars;
      }
    }

    if (additionalInstructions && additionalInstructions.trim()) {
      formData.additional_instructions = additionalInstructions.trim();
    }

    // Validate
    const result = researchSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0];
        if (field) {
          fieldErrors[field.toString()] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    // Submit
    try {
      await onSubmit(formData);
      // Clear form on success only if no initial values (to allow re-submission with pre-filled data)
      if (!initialValues) {
        setCities([]);
        setCityInput('');
        setSearchSequences('1');
        setMinRooms('');
        setMinCapacity('');
        setMinStars('');
        setAdditionalInstructions('');
      }
    } catch (error) {
      // Error handling is done by parent component
      console.error('Form submission error:', error);
    }
  };

  return (
    <Card>
      {!hideHeader && (
        <CardHeader>
          <CardTitle>Start New Research</CardTitle>
          <CardDescription>
            Discover hotels in any city using AI-powered web search. Specify your criteria and let our AI agent find matching venues.
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className={hideHeader ? 'pt-6' : ''}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Active Research Jobs Notification */}
          {matchingActiveJobs.length > 0 && (
            <Alert variant="info" title="Research Already in Progress">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>
                    {matchingActiveJobs.length} research {matchingActiveJobs.length === 1 ? 'job' : 'jobs'} 
                    {matchingActiveJobs.length === 1 ? ' is' : ' are'} already running for {matchingActiveJobs.length === 1 ? 'one of the selected' : 'some of the selected'} location{matchingActiveJobs.length === 1 ? '' : 's'}. 
                    You can still schedule additional research, but venues may overlap.
                  </span>
                </div>
                <div>
                  <Link
                    to="/organization/venues/ai-research"
                    className="text-sm font-medium text-info-700 hover:text-info-800 underline inline-flex items-center gap-1"
                  >
                    View AI Research Details
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </Link>
                </div>
              </div>
            </Alert>
          )}

          {/* Location Selection (Cities or Countries) */}
          <div className="space-y-2">
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">
              Locations <span className="text-error-600">*</span>
              <span className="text-gray-500 ml-2 font-normal text-xs">
                (Add up to 10 cities or countries)
              </span>
            </label>
            <LocationAutocomplete
              value={cityInput}
              onChange={setCityInput}
              onLocationSelect={handleLocationSelect}
              placeholder="Search for a city or country (e.g., Prague, Czech Republic or Spain)"
              types="cities"
              error={errors.search_cities}
              disabled={isSubmitting || cities.length >= 10}
            />
            
            {/* Selected Locations Display */}
            {cities.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-gray-600">Selected Locations:</p>
                <div className="flex flex-wrap gap-2">
                  {cities.map((locationData, idx) => {
                    const isCountry = locationData.location_type === 'country';
                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg"
                      >
                        {isCountry ? (
                          <Globe className="w-4 h-4 text-primary-600 flex-shrink-0" />
                        ) : (
                          <MapPin className="w-4 h-4 text-primary-600 flex-shrink-0" />
                        )}
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-primary-900">
                            {locationData.city}
                          </span>
                          <span className="text-xs text-gray-500">
                            {isCountry ? 'COUNTRY' : 'CITY'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCity(idx)}
                          disabled={isSubmitting}
                          className="text-primary-600 hover:text-primary-800 disabled:opacity-50"
                          aria-label={`Remove ${isCountry ? 'country' : 'city'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {errors.search_cities && (
              <p className="text-sm text-error-600">{errors.search_cities}</p>
            )}
            
            {cities.length === 0 && (
              <p className="text-sm text-gray-500">
                Start typing a city or country name to add locations to your research
              </p>
            )}
            
            {cities.length >= 10 && (
              <p className="text-sm text-warning-600">
                Maximum of 10 locations reached
              </p>
            )}
          </div>
          
          {/* Sequential Searches */}
          <div className="space-y-2">
            <label htmlFor="search_sequences" className="block text-sm font-medium text-gray-700 flex items-center gap-2">
              <Repeat className="w-4 h-4 text-primary-600" />
              Sequential Searches
              <span className="text-gray-500 ml-1 font-normal text-xs">
                (Run multiple searches per location to discover more venues)
              </span>
            </label>
            <Select
              id="search_sequences"
              value={searchSequences}
              onChange={(e) => setSearchSequences(e.target.value)}
              disabled={isSubmitting}
            >
              <option value="1">1 search (~10 venues per location)</option>
              <option value="2">2 searches (~20 venues per location)</option>
              <option value="3">3 searches (~30 venues per location)</option>
              <option value="4">4 searches (~40 venues per location)</option>
              <option value="5">5 searches (~50 venues per location)</option>
            </Select>
            <p className="text-xs text-gray-500">
              Each search discovers ~10 new venues. Sequential searches exclude previously found venues.
            </p>
            {cities.length > 0 && parseInt(searchSequences, 10) > 1 && (
              <p className="text-xs font-medium text-primary-700">
                Total jobs: {cities.length} × {searchSequences} = {cities.length * parseInt(searchSequences, 10)} searches
              </p>
            )}
          </div>

          {/* Filter Criteria */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary-600" />
              Filter Criteria (Optional)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Minimum Rooms */}
              <div className="space-y-2">
                <label htmlFor="min_rooms" className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  Minimum Rooms
                </label>
                <Input
                  id="min_rooms"
                  type="number"
                  value={minRooms}
                  onChange={(e) => setMinRooms(e.target.value)}
                  placeholder="e.g., 50"
                  min="1"
                  disabled={isSubmitting}
                />
                {errors.min_rooms && (
                  <p className="text-sm text-error-600">{errors.min_rooms}</p>
                )}
              </div>

              {/* Minimum Conference Capacity */}
              <div className="space-y-2">
                <label htmlFor="min_capacity" className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  Min Conference Capacity
                </label>
                <Input
                  id="min_capacity"
                  type="number"
                  value={minCapacity}
                  onChange={(e) => setMinCapacity(e.target.value)}
                  placeholder="e.g., 100"
                  min="1"
                  disabled={isSubmitting}
                />
                {errors.min_conference_capacity && (
                  <p className="text-sm text-error-600">{errors.min_conference_capacity}</p>
                )}
              </div>

              {/* Minimum Stars */}
              <div className="space-y-2">
                <label htmlFor="min_stars" className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Star className="w-4 h-4 text-gray-400" />
                  Minimum Star Rating
                </label>
                <Select
                  value={minStars}
                  onChange={(e) => setMinStars(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">Any rating</option>
                  <option value="1">1 star</option>
                  <option value="2">2 stars</option>
                  <option value="3">3 stars</option>
                  <option value="4">4 stars</option>
                  <option value="5">5 stars</option>
                </Select>
                {errors.min_stars && (
                  <p className="text-sm text-error-600">{errors.min_stars}</p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Instructions */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary-600" />
              Additional Instructions (Optional)
            </h3>
            
            <div className="space-y-2">
              <label htmlFor="additional_instructions" className="block text-sm font-medium text-gray-700">
                Custom AI Agent Instructions
              </label>
              <Textarea
                id="additional_instructions"
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                placeholder="e.g., 'Focus on boutique hotels with unique architecture' or 'Include only hotels with spa facilities'"
                rows={3}
                maxLength={500}
                disabled={isSubmitting}
                className="resize-none"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Provide specific instructions to guide the AI agent's search. These instructions will take priority over system defaults.
                </p>
                <p className="text-xs text-gray-400">
                  {additionalInstructions.length}/500
                </p>
              </div>
              {errors.additional_instructions && (
                <p className="text-sm text-error-600">{errors.additional_instructions}</p>
              )}
            </div>
          </div>

          {/* Helper Text */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <p className="text-sm text-primary-900">
              <strong>How it works:</strong> Our AI agent will search the web for hotels in your selected {cities.length === 1 ? 'location' : 'locations'} that match your criteria. 
              For cities, it searches within the city and surrounding areas. For countries, it discovers hotels across major cities and tourist destinations nationwide.
              The agent verifies information from multiple sources and creates venue records automatically. 
              {parseInt(searchSequences, 10) > 1 && (
                <span> Sequential searches will run one after another, excluding previously found venues.</span>
              )}
              {' '}Each search typically takes 30-60 seconds.
            </p>
            {cities.length > 0 && (
              <p className="text-sm text-primary-900 mt-2">
                <strong>Estimated time:</strong> {cities.length * parseInt(searchSequences, 10) * 45} seconds (~{Math.ceil(cities.length * parseInt(searchSequences, 10) * 45 / 60)} minutes)
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              type="submit"
              disabled={isSubmitting || cities.length === 0}
              loading={isSubmitting}
              size="lg"
            >
              {isSubmitting 
                ? 'Starting Research...' 
                : `Start Research (${cities.length * parseInt(searchSequences, 10)} ${cities.length * parseInt(searchSequences, 10) === 1 ? 'job' : 'jobs'})`
              }
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
